import React, { useState, useEffect } from 'react';
import { IconButton, withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import Header from '~/pages/Manager/Header';
import { useTranslation } from 'react-i18next';
import { AddPhotoAlternate } from '@material-ui/icons';
import styles from '../styles';
import {
  getEquipmentTypes,
  getCodes,
  getCodeTypes,
  getCodeGroups,
  getCodesTemplate,
  importCodes,
  exportCodes,
  setCodesValues,
  updateCodes,
  resetCodes,
  addCode,
  resetFileUploadErrors,
} from '~/store/manager/actions';
import PF2MDataTable from '~/components/PF2MDataTable';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import CodeIconModal from '~/components/CodeIconsModal/CodeIconModal';

const CodesTab = ({
  classes,
  codes,
  equipmentTypes,
  codeTypes,
  codeGroups,
  getEquipmentTypes: loadEquipmentTypes,
  getCodeTypes: loadCodeTypes,
  getCodeGroups: loadCodeGroups,
  getCodes: getAll,
  getCodesTemplate: getTemplateAction,
  exportCodes: exportAction,
  importCodes: importAction,
  setCodesValues: updateItem,
  updateCodes: update,
  resetCodes: reset,
  user,
}) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showDialogImport, setShowDialogImport] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const fileUploadErrors = useSelector(state => state.manager.fileUploadErrors);
  const [showDialogIcon, setShowDialogIcon] = useState(false);
  const [selectedIconData, setSelectedIconData] = useState(null);

  const isAdminF2M = user && user?.level === 1;

  useEffect(() => {
    async function fetchData() {
      await getAll(true);
      await loadEquipmentTypes();
      await loadCodeTypes();
      await loadCodeGroups(true);
    }
    if (!dataLoaded) {
      fetchData();
      setDataLoaded(true);
    }
  }, [dataLoaded, getAll, loadCodeGroups, loadCodeTypes, loadEquipmentTypes]);

  useEffect(() => {
    if (fileUploadErrors && fileUploadErrors.length > 0) {
      const message = fileUploadErrors.map((error) => {
        const [rowCol, description] = error.split('-');
        const [row, col] = rowCol.split(';');
        return `${translate('validation:Row')} ${row} ${translate('validation:Col')} ${col} - ${translate(`validation:${description}`)}`;
      });
      setDialogMessage(message.join('\r\n'));
      setShowDialogImport(true);
    }
  }, [fileUploadErrors]);

  /*
    0 = Invisível para todos  -> InvisibleForAll
    1 = Visível para todos    -> VisibleForAll
    2 = Visível operação      -> VisibleOperation
    3 = Visível pré checklist -> VisibleChecklist
  */
  const visibilityOptions = [
    {
      name: translate('common:InvisibleForAll'),
      value: 0,
    },
    {
      name: translate('common:VisibleForAll'),
      value: 1,
    },
    {
      name: translate('common:VisibleOperation'),
      value: 2,
    },
    {
      name: translate('common:VisibleChecklist'),
      value: 3,
    },
  ];

  const columns = [
    {
      editable: (_, rowData) => rowData && rowData.update_status === 'I',
      title: translate('common:EquipmentType'),
      field: 'id_equip',
      lookup: equipmentTypes,
    },
    {
      editable: (_, rowData) => rowData && rowData.update_status === 'I',
      title: translate('common:CodeType'),
      field: 'code_type',
      lookup: codeTypes.map(item => ({ ...item, name: translate(`common:${item.name}`) })),
      customOnChange: () => ({ id_group: '0' }),
    },
    {
      editable: (_, rowData) => rowData && rowData.update_status === 'I',
      title: translate('common:CodeGroup'),
      field: 'id_group',
      lookup: codeGroups,
      customType: 'filterLookup',
      lookupFilter: (lookup, rowData) => (
        String(lookup.code_type) === String(rowData.code_type)
        && String(lookup.id_equip) === String(rowData.id_equip)
      ),
      validate: ({ id_group }) => {
        if (!id_group || String(id_group) === '0' || String(id_group) === '') {
          return { isValid: false, helperText: translate('validation:RequiredField') };
        }
        return { isValid: true };
      },
      required: true,
    },
    {
      editable: 'always',
      title: translate('common:Code'),
      field: 'name',
      validate: ({ name: codeName }) => {
        const regex = /^.*[@#%*!=]+.*$/gi;

        if (!codeName) {
          return { isValid: false, helperText: translate('validation:RequiredField') };
        }

        if (regex.test(codeName)) {
          return { isValid: false, helperText: translate('validation:validChars') };
        }

        return { isValid: true };
      },
    },
    {
      editable: 'always',
      title: translate('common:Visibility'),
      field: 'is_visible',
      lookupKey: 'value',
      customType: 'filterLookup',
      lookup: visibilityOptions,
    },
    {
      editable: 'always', title: translate('common:LOGOUT'), field: 'is_logout', customType: 'boolean', filtering: false,
    },
    {
      editable: 'always',
      title: translate('common:STATUS'),
      field: 'active',
      customType: 'boolean',
      lookupKey: 'value',
      lookup: [
        { name: translate('common:Yes'), value: 1 },
        { name: translate('common:No'), value: 0 },
      ],
    },
    {
      editable: 'always',
      title: translate('common:CodeLocation'),
      field: 'code_location',
      customType: 'boolean',
      lookupKey: 'value',
      lookup: [
        { name: translate('common:Yes'), value: 1 },
        { name: translate('common:No'), value: 0 },
      ],
    },
    {
      editable: 'always',
      title: translate('common:HourMachine'),
      field: 'hour_machine',
      customType: 'boolean',
      lookupKey: 'value',
      lookup: [
        { name: translate('common:Yes'), value: 1 },
        { name: translate('common:No'), value: 0 },
      ],
    },
    {
      hidden: false,
      filtering: false,
      editable: 'never',
      title: translate('common:Icon'),
      render: (rowData) => {
        const isDisabled = rowData.name.trim() === ''
          || rowData.code_type === 2;

        return (
          <IconButton
            style={{ outline: 'none' }}
            onClick={() => {
              setSelectedIconData(rowData);
              setShowDialogIcon(true);
            }}
            disabled={isDisabled}
          >
            <AddPhotoAlternate
              className={classes.iconStyle}
              style={{
                color: isDisabled
                  ? 'rgba(0, 0, 0, 0.26)' : undefined,
              }}
            />
          </IconButton>
        );
      },
    },
    {
      editable: 'always',
      title: translate('common:ItMoves'),
      field: 'it_moves',
      customType: 'boolean',
      validate: ({ it_moves }) => {
        if (it_moves === null || it_moves === undefined) {
          return { isValid: false, helperText: translate('validation:RequiredField') };
        }

        return { isValid: true };
      },
      lookupKey: 'value',
      lookup: [
        { name: translate('common:Yes'), value: 1 },
        { name: translate('common:No'), value: 0 },
      ],
    },
  ];

  return (
    <div className={classes.tabContainer}>
      <Header
        getTemplateAction={getTemplateAction}
        importAction={importAction}
        exportAction={isAdminF2M && exportAction}
      />
      <hr style={{ margin: '0px' }} />
      <PF2MDataTable
        title={translate('common:Codes')}
        options={{
          showTitle: true,
          search: true,
          selection: true,
          filtering: true,
          pageSize: 20,
          pageSizeOptions: [20, 50, 100],
          hideDelete: true,
          editableRowValidation: rowData => (rowData.id < 0 && rowData.update_status !== 'I'),
          maxBodyHeight: 'calc(100vh - 350px)',
          minBodyHeight: 'calc(100vh - 350px)',
        }}
        onAdd={newData => dispatch(addCode(newData))}
        onChange={(newData) => {
          newData.forEach((row) => {
            updateItem({ ...row, name: row.name.trim() });
          });
        }}
        onDiscard={() => reset()}
        onApply={() => update(codes)}
        columns={columns}
        data={codes}
      />
      <PF2MAlertDialog
        title={translate('validation:ImportErrors')}
        description={dialogMessage}
        open={showDialogImport}
        onConfirm={() => {
          setShowDialogImport(false);
          dispatch(resetFileUploadErrors());
        }}
        onClose={() => setShowDialogImport(false)}
        hasCancel={false}
      />

      <CodeIconModal
        open={showDialogIcon}
        onClose={() => setShowDialogIcon(false)}
        modalData={selectedIconData}
      />
    </div>
  );
};

CodesTab.propTypes = {
  classes: PropTypes.object.isRequired,
  codes: PropTypes.array,
  equipmentTypes: PropTypes.array,
  codeTypes: PropTypes.array,
  codeGroups: PropTypes.array,
  getEquipmentTypes: PropTypes.func,
  getCodes: PropTypes.func,
  getCodeTypes: PropTypes.func,
  getCodeGroups: PropTypes.func,
  getCodesTemplate: PropTypes.func,
  exportCodes: PropTypes.func,
  importCodes: PropTypes.func,
  setCodesValues: PropTypes.func,
  updateCodes: PropTypes.func,
  resetCodes: PropTypes.func,
  user: PropTypes.object,
};

CodesTab.defaultProps = {
  codes: [],
  equipmentTypes: [],
  codeTypes: [],
  codeGroups: [],
  getEquipmentTypes: () => { },
  getCodes: () => { },
  getCodeTypes: () => { },
  getCodeGroups: () => { },
  getCodesTemplate: () => { },
  exportCodes: () => { },
  importCodes: () => { },
  setCodesValues: null,
  updateCodes: null,
  resetCodes: null,
  user: {},
};

const mapStateToProps = state => ({
  equipmentTypes: state.manager.equipmentTypes,
  codes: state.manager.codes,
  codeTypes: state.manager.codeTypes,
  codeGroups: state.manager.codeGroups,
  isCodesDirty: state.manager.isCodesDirty,
  user: state.auth.user,
});

const mapDispatchToProps = {
  getEquipmentTypes,
  getCodes,
  getCodeTypes,
  getCodeGroups,
  getCodesTemplate,
  exportCodes,
  importCodes,
  setCodesValues,
  updateCodes,
  resetCodes,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(CodesTab));
